import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLenis } from "lenis/react";
import { ArrowLeft, Check } from "lucide-react";
import SchedulingCalendar from "@/components/SchedulingCalendar";
import { Reveal } from "@/components/Reveal";

const EXPECTATIONS = [
  "A clear read on your biggest growth blocker right now",
  "One or two moves you can make this week — whether we work together or not",
  "Zero pitch. Fifteen minutes of clarity, then you decide",
];

const BookPage = () => {
  const lenis = useLenis();

  useEffect(() => {
    lenis?.scrollTo(0, { immediate: true });
    document.title = "Book a Free Strategy Call | Makkar Growth Partners";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", "Book a free 15-minute strategy call with Aspen Makkar. A clear read on your biggest growth blocker — no pitch, just clarity.");
  }, [lenis]);

  return (
    <main data-testid="book-page" className="bg-paper text-ink min-h-screen flex flex-col">
      <header className="border-b border-line bg-paper/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="mx-auto max-w-[100rem] flex items-center justify-between px-6 md:px-12 py-4">
          <Link to="/" data-testid="book-nav-logo" className="leading-none">
            <span className="font-serif text-xl md:text-2xl font-medium tracking-tight">Makkar</span>
            <span className="block text-[10px] uppercase tracking-[0.3em] text-ash mt-1">Growth Partners</span>
          </Link>
          <Link
            to="/"
            data-testid="book-back-btn"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ash hover:text-ink transition-colors duration-300"
          >
            <ArrowLeft size={14} /> Back to site
          </Link>
        </div>
      </header>

      <section className="flex-1 px-6 md:px-12 py-16 md:py-24">
        <div className="mx-auto max-w-[100rem] grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24">
          <div>
            <Reveal>
              <p className="overline-label">Free · 15 Minutes · Google Meet</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-6 font-serif text-4xl md:text-6xl font-light tracking-tight leading-[1.05]">
                Let's find your <em className="text-taupe">next stage of growth.</em>
              </h1>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 text-base md:text-lg font-light text-ash leading-relaxed max-w-md">
                A focused conversation with Aspen Makkar — 17 years of brand, growth,
                and demand-generation leadership across CPG, fintech, and B2B SaaS.
              </p>
            </Reveal>
            <div className="mt-10">
              <p className="text-[10px] uppercase tracking-[0.25em] text-taupe mb-4">What you'll walk away with</p>
              {EXPECTATIONS.map((e, i) => (
                <Reveal key={e} delay={0.2 + i * 0.05}>
                  <p className="flex gap-4 border-t border-line py-4 text-sm md:text-base font-light text-ink/80">
                    <Check size={16} className="text-taupe shrink-0 mt-0.5" />
                    {e}
                  </p>
                </Reveal>
              ))}
              <div className="border-t border-line" />
            </div>
            <Reveal delay={0.35}>
              <p className="mt-8 font-serif italic text-lg text-ash">
                Availability is intentionally limited — only a few client spots open each quarter.
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <SchedulingCalendar />
            <p className="mt-4 text-center text-[10px] uppercase tracking-[0.2em] text-ash">
              Instant confirmation · Calendar invite sent automatically
            </p>
          </Reveal>
        </div>
      </section>

      <footer className="border-t border-line px-6 md:px-12 py-8">
        <div className="mx-auto max-w-[100rem] flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[10px] uppercase tracking-[0.25em] text-ash">
            © 2026 Makkar Growth Partners
          </p>
          <a
            data-testid="book-email-link"
            href="mailto:aspenmakkarcmo@gmail.com"
            className="text-xs font-light text-ash hover:text-ink transition-colors duration-300"
          >
            Prefer email? aspenmakkarcmo@gmail.com
          </a>
        </div>
      </footer>
    </main>
  );
};

export default BookPage;
